import { useQuery } from '@apollo/client';
import React, { useEffect, useMemo, useState } from 'react';

import { eluveAdminHasuraContext } from '@eluve/apollo-client';
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Stack,
  Text,
  useToast,
} from '@eluve/components';
import { FragmentOf } from '@eluve/graphql.tada';

import {
  getTemplateBundleDisplayName,
  templateBundleListItemFragment,
} from '../../../template-bundles/template-bundle.operations';

import { AffectedUsersPreview } from './AffectedUsersPreview';
import { BulkResultSummary } from './BulkResultSummary';
import { getTenantUsersForBulkBundleQuery } from './bulk-default-bundle.operations';
import {
  type BulkTargetUser,
  useBulkUpdateDefaultBundle,
} from './useBulkUpdateDefaultBundle';

type TemplateBundleListItem = FragmentOf<typeof templateBundleListItemFragment>;

interface MigrateDefaultBundleDialogProps {
  tenantId: string;
  tenantName: string;
  bundles: TemplateBundleListItem[];
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const NO_SELECTION = '';

export const MigrateDefaultBundleDialog: React.FC<
  MigrateDefaultBundleDialogProps
> = ({ tenantId, tenantName, bundles, open, onOpenChange }) => {
  const toast = useToast();

  const [sourceBundleId, setSourceBundleId] = useState<string>(NO_SELECTION);
  const [targetBundleId, setTargetBundleId] = useState<string>(NO_SELECTION);

  const { data, loading: loadingUsers } = useQuery(
    getTenantUsersForBulkBundleQuery,
    {
      variables: { tenantId },
      context: eluveAdminHasuraContext,
      skip: !open,
      fetchPolicy: 'cache-and-network',
    },
  );

  const tenantUsers = useMemo(
    () => data?.tenantsByPk?.tenantUsers ?? [],
    [data],
  );

  const affectedUsers = useMemo(() => {
    if (!sourceBundleId) return [];
    return tenantUsers.filter(
      (tu) => tu.settings?.defaultTemplateBundleId === sourceBundleId,
    );
  }, [tenantUsers, sourceBundleId]);

  const previewRows = useMemo(
    () =>
      affectedUsers.map((tu) => ({
        userId: tu.userId,
        firstName: tu.user?.firstName ?? null,
        lastName: tu.user?.lastName ?? null,
        email: tu.user?.email ?? null,
        specialty: tu.user?.specialty ?? null,
      })),
    [affectedUsers],
  );

  const { status, results, run, reset } = useBulkUpdateDefaultBundle(tenantId);

  // Reset transient form state whenever the dialog is closed.
  useEffect(() => {
    if (!open) {
      setSourceBundleId(NO_SELECTION);
      setTargetBundleId(NO_SELECTION);
      reset();
    }
  }, [open, reset]);

  const sourceBundle = useMemo(
    () => bundles.find((b) => b.id === sourceBundleId) ?? null,
    [bundles, sourceBundleId],
  );
  const targetBundle = useMemo(
    () => bundles.find((b) => b.id === targetBundleId) ?? null,
    [bundles, targetBundleId],
  );

  const sameBundle =
    sourceBundleId !== NO_SELECTION && sourceBundleId === targetBundleId;
  const cohortEmpty =
    sourceBundleId !== NO_SELECTION && previewRows.length === 0;
  const isRunning = status === 'running';
  const isDone = status === 'done';

  const canSubmit =
    !isRunning &&
    !isDone &&
    sourceBundleId !== NO_SELECTION &&
    targetBundleId !== NO_SELECTION &&
    !sameBundle &&
    previewRows.length > 0;

  const handleSubmit = async () => {
    if (!canSubmit || !targetBundle) return;
    const targets: BulkTargetUser[] = affectedUsers.map((tu) => ({
      userId: tu.userId,
      firstName: tu.user?.firstName ?? null,
      lastName: tu.user?.lastName ?? null,
      email: tu.user?.email ?? null,
    }));
    const settled = await run(targets, targetBundle.id);
    const failed = settled.filter((r) => !r.ok).length;
    if (failed === 0) {
      toast.success(`Migrated ${settled.length} user(s) to new default bundle`);
    } else {
      toast.error(
        `Migrated ${settled.length - failed}/${settled.length}; ${failed} failed`,
      );
    }
  };

  // Block Escape/X dismissal mid-run: closing while in flight would reset the
  // hook's status/results to idle, but the per-row mutations would still settle
  // and write back to the (now stale) state. A subsequent run would race over
  // the same state and silently overwrite the first run's results.
  const handleOpenChange = (next: boolean) => {
    if (!next && isRunning) return;
    onOpenChange(next);
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent size="2xl">
        <DialogHeader>
          <DialogTitle>Migrate Default Bundle</DialogTitle>
          <DialogDescription>
            Tenant: <strong>{tenantName}</strong>
          </DialogDescription>
        </DialogHeader>

        <Stack gap={4} align="stretch">
          <Stack gap={1}>
            <Label htmlFor="source-bundle">Source bundle</Label>
            <Select
              value={sourceBundleId || undefined}
              onValueChange={setSourceBundleId}
              disabled={loadingUsers || isRunning || isDone}
            >
              <SelectTrigger id="source-bundle" className="w-full">
                <SelectValue placeholder="Select source bundle" />
              </SelectTrigger>
              <SelectContent>
                {bundles.map((b) => (
                  <SelectItem key={b.id} value={b.id}>
                    {getTemplateBundleDisplayName(b)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Stack>

          {sourceBundleId !== NO_SELECTION && (
            <AffectedUsersPreview
              users={previewRows}
              emptyMessage={
                loadingUsers
                  ? 'Loading users...'
                  : `No users in this tenant currently default to "${
                      sourceBundle
                        ? getTemplateBundleDisplayName(sourceBundle)
                        : ''
                    }".`
              }
            />
          )}

          <Stack gap={1}>
            <Label htmlFor="target-bundle">Target bundle</Label>
            <Select
              value={targetBundleId || undefined}
              onValueChange={setTargetBundleId}
              disabled={
                sourceBundleId === NO_SELECTION ||
                isRunning ||
                isDone ||
                cohortEmpty
              }
            >
              <SelectTrigger id="target-bundle" className="w-full">
                <SelectValue placeholder="Select target bundle" />
              </SelectTrigger>
              <SelectContent>
                {bundles.map((b) => (
                  <SelectItem
                    key={b.id}
                    value={b.id}
                    disabled={b.id === sourceBundleId}
                  >
                    {getTemplateBundleDisplayName(b)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {sameBundle && (
              <Text size="xs" className="text-text-warning-subtle">
                Source and target must differ.
              </Text>
            )}
          </Stack>

          {isDone && <BulkResultSummary results={results} />}
        </Stack>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => handleOpenChange(false)}
            disabled={isRunning}
          >
            {isDone ? 'Close' : 'Cancel'}
          </Button>
          {!isDone && (
            <Button
              onClick={handleSubmit}
              disabled={!canSubmit}
              loading={isRunning}
            >
              {isRunning
                ? 'Migrating...'
                : `Confirm migration of ${previewRows.length} user${
                    previewRows.length === 1 ? '' : 's'
                  }`}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
